import { useState, useEffect, useCallback, useMemo } from 'react';
import { FiSearch, FiDownload, FiEye, FiEdit, FiTrash2, FiMail, FiPhone, FiMapPin, FiCalendar, FiCheckCircle, FiXCircle, FiX, FiSave } from 'react-icons/fi';
import { universityService, getUploadUrl } from '../services/apiService';
import { Country, State } from 'country-state-city';
import * as XLSX from 'xlsx';

const ALL_COUNTRIES = Country.getAllCountries().map((c) => ({ isoCode: c.isoCode, name: c.name }));

const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'];
const STATUSES = ['Vice-Chancellor','Registrar','Principal','Dean','Director','HOD','Associate Professor','Assistant Professor','Faculty','Administrator'];

const UniversitiesList = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [editUniversity, setEditUniversity] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, total: 1, totalItems: 0 });

  const editCountryIso = useMemo(() => {
    const found = ALL_COUNTRIES.find((c) => c.name === editForm.country);
    return found ? found.isoCode : null;
  }, [editForm.country]);

  const editStates = useMemo(() => {
    if (!editCountryIso) return [];
    return State.getStatesOfCountry(editCountryIso);
  }, [editCountryIso]);

  const fetchUniversities = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        status: filterStatus,
        page: pagination.current,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };
      if (filterVerified === 'verified') params.isVerified = true;
      else if (filterVerified === 'unverified') params.isVerified = false;

      const response = await universityService.getAllUniversities(params);
      if (response?.data) {
        setUniversities(response.data);
        if (response.pagination) {
          setPagination({ current: response.pagination.current, total: response.pagination.total, totalItems: response.pagination.totalItems });
        }
      }
    } catch (err) {
      console.error('Error fetching universities:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterVerified, pagination.current]);

  useEffect(() => { fetchUniversities(); }, [fetchUniversities]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) { fetchUniversities(); return; }
    try {
      setLoading(true);
      const response = await universityService.getAllUniversities({ search: searchTerm, page: 1, limit: 20 });
      if (response?.data) {
        setUniversities(response.data);
        if (response.pagination) setPagination({ current: response.pagination.current, total: response.pagination.total, totalItems: response.pagination.totalItems });
      }
    } catch (err) {
      console.error('Error searching universities:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await universityService.getAllUniversities({ limit: 10000, isVerified: true });
      const all = response.data || [];
      if (!all.length) { alert('No universities to export'); return; }
      const exportData = all.map(uni => ({
        'Institute Name': uni.instituteName || '',
        'University': uni.university || '',
        'Contact Person': uni.contactPerson || '',
        'Salutation': uni.salutation || '',
        'Status': uni.status || '',
        'Email': uni.email || '',
        'Mobile': uni.mobileNumber || '',
        'City': uni.instituteCity || '',
        'State': uni.state || '',
        'Country': uni.country || '',
        'Website': uni.website || '',
        'Verified': uni.isVerified ? 'Yes' : 'No',
        'Join Date': uni.legacyJoinDate ? new Date(uni.legacyJoinDate).toLocaleDateString() : new Date(uni.createdAt).toLocaleDateString(),
        'Legacy ID': uni.legacyId || '',
        'Legacy Points': uni.legacyPoints || 0,
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Universities');
      XLSX.writeFile(wb, `universities_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Failed to download universities list');
    }
  };

  const handlePageChange = (newPage) => setPagination(prev => ({ ...prev, current: newPage }));

  // --- Selection ---
  const toggleSelect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUniversities.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredUniversities.map(u => u._id)));
  };

  // --- Delete ---
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this university? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await universityService.deleteUniversity(id);
      setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      fetchUniversities();
    } catch (err) {
      alert('Failed to delete university.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected universities? This cannot be undone.`)) return;
    setIsDeleting(true);
    try {
      await universityService.bulkDeleteUniversities([...selectedIds]);
      setSelectedIds(new Set());
      fetchUniversities();
    } catch (err) {
      alert('Failed to bulk delete universities.');
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Edit ---
  const openEdit = (uni) => {
    setEditUniversity(uni);
    setEditForm({
      salutation: uni.salutation || '',
      contactPerson: uni.contactPerson || '',
      status: uni.status || '',
      email: uni.email || '',
      mobileNumber: uni.mobileNumber || '',
      instituteName: uni.instituteName || '',
      university: uni.university || '',
      instituteCity: uni.instituteCity || '',
      state: uni.state || '',
      country: uni.country || '',
      zipCode: uni.zipCode || '',
      website: uni.website || '',
      description: uni.description || '',
      isVerified: uni.isVerified || false,
      adminNotes: uni.adminNotes || '',
    });
    setSaveError('');
  };

  const handleEditSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      await universityService.updateUniversity(editUniversity._id, editForm);
      setEditUniversity(null);
      fetchUniversities();
    } catch (err) {
      setSaveError(err.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredUniversities = universities.filter(uni => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return uni.instituteName?.toLowerCase().includes(s) || uni.university?.toLowerCase().includes(s) ||
      uni.email?.toLowerCase().includes(s) || uni.contactPerson?.toLowerCase().includes(s) || uni.instituteCity?.toLowerCase().includes(s);
  });

  const allSelected = filteredUniversities.length > 0 && selectedIds.size === filteredUniversities.length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Registered Universities</h1>
        <p className="mt-1 text-sm text-gray-600">Total: {pagination.totalItems} universities</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <input type="text" placeholder="Search by name, email, city..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent">
            <option value="all">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterVerified} onChange={e => setFilterVerified(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent">
            <option value="all">All Universities</option>
            <option value="verified">Verified Only</option>
            <option value="unverified">Unverified Only</option>
          </select>
        </div>
        <div className="mt-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <button onClick={handleSearch}
              className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center">
              <FiSearch className="mr-2" /> Search
            </button>
            {selectedIds.size > 0 && (
              <button onClick={handleBulkDelete} disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center disabled:opacity-60">
                <FiTrash2 className="mr-2" /> Delete Selected ({selectedIds.size})
              </button>
            )}
          </div>
          <button onClick={downloadExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
            <FiDownload className="mr-2" /> Export to Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#166534]"></div>
            <p className="mt-2 text-gray-600">Loading universities...</p>
          </div>
        ) : filteredUniversities.length === 0 ? (
          <div className="text-center py-12"><p className="text-gray-600">No universities found</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institute</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Person</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUniversities.map(uni => (
                    <tr key={uni._id} className={`hover:bg-gray-50 ${selectedIds.has(uni._id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input type="checkbox" checked={selectedIds.has(uni._id)} onChange={() => toggleSelect(uni._id)}
                          className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {uni.logo ? (
                            <img src={getUploadUrl(uni.logo)} alt="" className="w-10 h-10 rounded-full mr-3 object-cover border-2 border-gray-200" />
                          ) : (
                            <div className="w-10 h-10 rounded-full mr-3 bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-500 text-xs font-medium">{uni.instituteName?.charAt(0) || 'U'}</span>
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{uni.instituteName}</div>
                            <div className="text-sm text-gray-500">{uni.university}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{uni.salutation} {uni.contactPerson}</div>
                        <div className="text-sm text-gray-500">{uni.status}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{uni.email}</div>
                        <div className="text-sm text-gray-500">{uni.mobileNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{uni.instituteCity}</div>
                        <div className="text-sm text-gray-500">{uni.state}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">{uni.status}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {uni.isVerified ? (
                          <span className="flex items-center text-green-600"><FiCheckCircle className="mr-1" /><span className="text-sm">Yes</span></span>
                        ) : (
                          <span className="flex items-center text-yellow-600"><FiXCircle className="mr-1" /><span className="text-sm">No</span></span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-3">
                          <button onClick={() => setSelectedUniversity(uni)} className="text-gray-500 hover:text-gray-700" title="View"><FiEye /></button>
                          <button onClick={() => openEdit(uni)} className="text-[#166534] hover:text-[#0e3d1f]" title="Edit"><FiEdit /></button>
                          <button onClick={() => handleDelete(uni._id)} disabled={isDeleting} className="text-red-500 hover:text-red-700 disabled:opacity-50" title="Delete"><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.current - 1) * 20 + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(pagination.current * 20, pagination.totalItems)}</span> of{' '}
                    <span className="font-medium">{pagination.totalItems}</span> results
                  </p>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button onClick={() => handlePageChange(pagination.current - 1)} disabled={pagination.current === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                    {[...Array(Math.min(pagination.total, 5))].map((_, i) => {
                      const p = i + 1;
                      return (
                        <button key={p} onClick={() => handlePageChange(p)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.current === p ? 'z-10 bg-[#166534] border-[#166534] text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                          {p}
                        </button>
                      );
                    })}
                    <button onClick={() => handlePageChange(pagination.current + 1)} disabled={pagination.current === pagination.total}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Next</button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Modal */}
      {selectedUniversity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  {selectedUniversity.logo ? (
                    <img src={getUploadUrl(selectedUniversity.logo)} alt="" className="w-16 h-16 rounded-full mr-4 object-cover border-2 border-gray-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500 text-lg font-medium">{selectedUniversity.instituteName?.charAt(0) || 'U'}</span>
                    </div>
                  )}
                  <h2 className="text-2xl font-semibold text-gray-900">University Details</h2>
                </div>
                <button onClick={() => setSelectedUniversity(null)} className="text-gray-400 hover:text-gray-600">
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              {(selectedUniversity.logo || selectedUniversity.campusPhoto) && (
                <div className="mb-6 grid grid-cols-2 gap-4">
                  {selectedUniversity.logo && (
                    <div><p className="text-sm text-gray-500 mb-2">University Logo</p>
                      <img src={getUploadUrl(selectedUniversity.logo)} alt="Logo" className="w-full h-32 object-contain border rounded-lg bg-gray-50" /></div>
                  )}
                  {selectedUniversity.campusPhoto && (
                    <div><p className="text-sm text-gray-500 mb-2">Campus Photo</p>
                      <img src={getUploadUrl(selectedUniversity.campusPhoto)} alt="Campus" className="w-full h-32 object-cover border rounded-lg" /></div>
                  )}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Institute Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">Institute Name</p><p className="text-sm font-medium">{selectedUniversity.instituteName}</p></div>
                    <div><p className="text-sm text-gray-500">University</p><p className="text-sm font-medium">{selectedUniversity.university}</p></div>
                    <div><p className="text-sm text-gray-500">Website</p><a href={selectedUniversity.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#166534] hover:underline">{selectedUniversity.website}</a></div>
                    <div><p className="text-sm text-gray-500">Status</p><p className="text-sm font-medium">{selectedUniversity.status}</p></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">Contact Person</p><p className="text-sm font-medium">{selectedUniversity.salutation} {selectedUniversity.contactPerson}</p></div>
                    <div><p className="text-sm text-gray-500">Email</p><p className="text-sm font-medium flex items-center"><FiMail className="mr-1" />{selectedUniversity.email}</p></div>
                    <div><p className="text-sm text-gray-500">Mobile</p><p className="text-sm font-medium flex items-center"><FiPhone className="mr-1" />{selectedUniversity.mobileNumber}</p></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Location</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">City</p><p className="text-sm font-medium flex items-center"><FiMapPin className="mr-1" />{selectedUniversity.instituteCity}</p></div>
                    <div><p className="text-sm text-gray-500">State</p><p className="text-sm font-medium">{selectedUniversity.state}</p></div>
                    <div><p className="text-sm text-gray-500">Country</p><p className="text-sm font-medium">{selectedUniversity.country}</p></div>
                    <div><p className="text-sm text-gray-500">Zip Code</p><p className="text-sm font-medium">{selectedUniversity.zipCode}</p></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Additional</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">Verified</p><p className="text-sm font-medium">{selectedUniversity.isVerified ? 'Yes' : 'No'}</p></div>
                    <div><p className="text-sm text-gray-500">Joined</p><p className="text-sm font-medium flex items-center"><FiCalendar className="mr-1" />{selectedUniversity.legacyJoinDate ? new Date(selectedUniversity.legacyJoinDate).toLocaleDateString() : new Date(selectedUniversity.createdAt).toLocaleDateString()}</p></div>
                    {selectedUniversity.legacyId && <div><p className="text-sm text-gray-500">Legacy ID</p><p className="text-sm font-medium">{selectedUniversity.legacyId}</p></div>}
                    {selectedUniversity.legacyPoints > 0 && <div><p className="text-sm text-gray-500">Legacy Points</p><p className="text-sm font-medium">{selectedUniversity.legacyPoints}</p></div>}
                  </div>
                  {selectedUniversity.description && <div className="mt-2"><p className="text-sm text-gray-500">Description</p><p className="text-sm">{selectedUniversity.description}</p></div>}
                  {selectedUniversity.adminNotes && <div className="mt-2"><p className="text-sm text-gray-500">Admin Notes</p><p className="text-sm">{selectedUniversity.adminNotes}</p></div>}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => { setSelectedUniversity(null); openEdit(selectedUniversity); }}
                  className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center">
                  <FiEdit className="mr-2" /> Edit
                </button>
                <button onClick={() => setSelectedUniversity(null)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editUniversity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Edit University</h2>
                <button onClick={() => setEditUniversity(null)} className="text-gray-400 hover:text-gray-600"><FiX className="h-6 w-6" /></button>
              </div>
              {saveError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{saveError}</div>}

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Contact Person</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Salutation</label>
                      <select value={editForm.salutation} onChange={e => setEditForm(f => ({ ...f, salutation: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent">
                        {SALUTATIONS.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Contact Person</label>
                      <input value={editForm.contactPerson} onChange={e => setEditForm(f => ({ ...f, contactPerson: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Designation</label>
                      <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent">
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                      <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Mobile</label>
                      <input value={editForm.mobileNumber} onChange={e => setEditForm(f => ({ ...f, mobileNumber: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Institute</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Institute Name</label>
                      <input value={editForm.instituteName} onChange={e => setEditForm(f => ({ ...f, instituteName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">University</label>
                      <input value={editForm.university} onChange={e => setEditForm(f => ({ ...f, university: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                      <input value={editForm.instituteCity} onChange={e => setEditForm(f => ({ ...f, instituteCity: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
                      <select
                        value={editForm.country || ''}
                        onChange={e => setEditForm(f => ({ ...f, country: e.target.value, state: '' }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent bg-white"
                      >
                        <option value="">Select a country</option>
                        {ALL_COUNTRIES.map((c) => (
                          <option key={c.isoCode} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">State / Province</label>
                      {editStates.length > 0 ? (
                        <select
                          value={editForm.state || ''}
                          onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent bg-white"
                        >
                          <option value="">Select a state</option>
                          {editStates.map((s) => (
                            <option key={s.isoCode} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={editForm.state || ''}
                          onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))}
                          placeholder={editForm.country ? 'Enter state / province' : 'Select a country first'}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Zip Code</label>
                      <input value={editForm.zipCode} onChange={e => setEditForm(f => ({ ...f, zipCode: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Website</label>
                      <input value={editForm.website} onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                      <textarea rows={2} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Admin</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="uni-verified" checked={editForm.isVerified} onChange={e => setEditForm(f => ({ ...f, isVerified: e.target.checked }))}
                        className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                      <label htmlFor="uni-verified" className="text-sm text-gray-700">Mark as Verified</label>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Admin Notes</label>
                      <textarea rows={2} value={editForm.adminNotes} onChange={e => setEditForm(f => ({ ...f, adminNotes: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditUniversity(null)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
                <button onClick={handleEditSave} disabled={isSaving}
                  className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center disabled:opacity-60">
                  <FiSave className="mr-2" /> {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversitiesList;
